import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cguardFetcher, cguardStreamFetcher } from '../runtime/fetcher';

export type ExportPhase = 'queued' | 'fetching' | 'encoding' | 'ready' | 'failed' | 'cancelled' | 'expired';
export interface EvidenceExport {
  id: string; userId: string; cameraId: string; siteId: string; cameraName: string;
  fromUtc: string; toUtc: string; createdAtUtc: string; expiresAtUtc: string;
  phase: ExportPhase; progress: number; error: string | null; sha256: string | null; byteSize: number;
}
export interface RecordingRange { from: string; to: string }
export interface RecordingCoverage { supported: boolean; segments: RecordingRange[]; gaps: RecordingRange[] }
export interface ExportInput { requestId: string; cameraId?: string; incidentId?: string; from?: string; to?: string }
function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid recorder response.');
  return value as Record<string, unknown>;
}
function string(value: unknown): string { if (typeof value !== 'string') throw new Error('Invalid recorder field.'); return value; }
function number(value: unknown): number { if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error('Invalid recorder number.'); return value; }
export function parseExport(value: unknown): EvidenceExport {
  const job = record(value);
  const phase = string(job.phase);
  if (phase !== 'queued' && phase !== 'fetching' && phase !== 'encoding' && phase !== 'ready' && phase !== 'failed' && phase !== 'cancelled' && phase !== 'expired') throw new Error('Invalid export phase.');
  return { id: string(job.id), userId: string(job.userId), cameraId: string(job.cameraId), siteId: string(job.siteId), cameraName: string(job.cameraName),
    fromUtc: string(job.fromUtc), toUtc: string(job.toUtc), createdAtUtc: string(job.createdAtUtc), expiresAtUtc: string(job.expiresAtUtc), phase,
    progress: number(job.progress), byteSize: number(job.byteSize), error: job.error == null ? null : string(job.error), sha256: job.sha256 == null ? null : string(job.sha256) };
}
async function json(path: string, init: RequestInit = {}): Promise<unknown> {
  return (await cguardFetcher<{ data: unknown }>(path, init)).data;
}
export const evidenceKey = ['evidence-exports'] as const;
export function useEvidenceExports(siteId: string, search: string, offset: number, mine = true) {
  return useQuery({ queryKey: [...evidenceKey, siteId, search, offset, mine], queryFn: async ({ signal }) => {
    const params = new URLSearchParams({ search, offset: String(offset), mine: String(mine) }); if (siteId) params.set('siteId', siteId);
    const value = await json(`/v1/recordings/exports?${params}`, { signal });
    if (!Array.isArray(value)) throw new Error('Invalid exports response.');
    return value.map(parseExport);
  }, refetchInterval: 15_000 });
}
export function useEvidenceExport(id: string | null) {
  return useQuery({ queryKey: [...evidenceKey, id], enabled: id !== null, queryFn: async ({ signal }) => parseExport(await json(`/v1/recordings/exports/${id}`, { signal })),
    refetchInterval: (query) => query.state.data && ['ready', 'failed', 'cancelled', 'expired'].includes(query.state.data.phase) ? false : 3000 });
}
export function useStartEvidenceExport() {
  const cache = useQueryClient();
  return useMutation({ mutationFn: async (input: ExportInput) => parseExport(await json('/v1/recordings/exports', { method: 'POST', body: JSON.stringify(input) })),
    onSuccess: () => cache.invalidateQueries({ queryKey: evidenceKey }) });
}
export async function deleteEvidenceExport(id: string): Promise<void> { await json(`/v1/recordings/exports/${encodeURIComponent(id)}`, { method: 'DELETE' }); }
export async function recordingCoverage(cameraId: string, from: string, to: string, signal?: AbortSignal): Promise<RecordingCoverage> {
  const data = record(await json(`/v1/cameras/${encodeURIComponent(cameraId)}/recordings?${new URLSearchParams({ from, to })}`, signal ? { signal } : {}));
  const ranges = (value: unknown): RecordingRange[] => {
    if (!Array.isArray(value)) throw new Error('Invalid recording segments.');
    return value.map((item: unknown) => { const r = record(item); return { from: string(r.from), to: string(r.to) }; });
  };
  if (typeof data.supported !== 'boolean') throw new Error('Invalid coverage response.');
  return { supported: data.supported, segments: ranges(data.segments), gaps: ranges(data.gaps) };
}
export async function stopCameraPlayback(url: string): Promise<void> {
  const token = new URL(url, 'https://central.invalid').searchParams.get('token');
  if (token) await json('/v1/cameras/playback/stop', { method: 'POST', body: JSON.stringify({ token }), keepalive: true });
}
export async function subscribeExportChanges(signal: AbortSignal, changed: (job: EvidenceExport) => void, mine = true): Promise<void> {
  const response = await cguardStreamFetcher(`/v1/recordings/exports/events?mine=${mine}`, { signal, headers: { accept: 'text/event-stream' } });
  const reader = response.body?.getReader(); if (!reader) throw new Error('Export feed unavailable.');
  const decoder = new TextDecoder(); let buffer = '';
  try { for (;;) {
    const { value, done } = await reader.read(); if (done) return;
    buffer += decoder.decode(value, { stream: true });
    if (buffer.length > 262144) throw new Error('Export feed exceeded message limit.');
    let end: number;
    while ((end = buffer.indexOf('\n\n')) >= 0) {
      const event = buffer.slice(0, end); buffer = buffer.slice(end + 2);
      if (event.startsWith('event: JobChanged\n')) changed(parseExport(JSON.parse(event.slice(event.indexOf('data: ') + 6))));
    }
  } } finally { await reader.cancel(); }
}
export async function downloadEvidence(id: string, signal: AbortSignal, progress: (percent: number) => void): Promise<Blob> {
  const grant = record(await json(`/v1/recordings/exports/${encodeURIComponent(id)}/download`, { method: 'POST', signal }));
  const expected = number(grant.byteSize); const hash = string(grant.sha256);
  if (expected <= 0 || expected > 1073741824 || !/^[a-fA-F0-9]{64}$/.test(hash)) throw new Error('Invalid evidence manifest.');
  const response = await cguardStreamFetcher(string(grant.url), { signal });
  const reader = response.body?.getReader(); if (!reader) throw new Error('Evidence stream unavailable.');
  const chunks: BlobPart[] = []; let received = 0;
  try { for (;;) {
    const { value, done } = await reader.read(); if (done) break;
    received += value.byteLength; if (received > expected) throw new Error('Evidence exceeds its manifest size.');
    chunks.push(new Uint8Array(value).buffer); progress(Math.round(received * 100 / expected));
  } } finally { await reader.cancel(); }
  if (received !== expected) throw new Error('Evidence download is incomplete.');
  const blob = new Blob(chunks, { type: 'video/mp4' });
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  if (signal.aborted) throw new DOMException('Download cancelled', 'AbortError');
  const actual = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  if (actual !== hash.toLowerCase()) throw new Error('Evidence SHA-256 does not match.');
  return blob;
}
